import React, { useEffect, useState } from 'react';
import { api, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Download, CheckCircle2, RotateCcw, XCircle, ClipboardList } from 'lucide-react';

const KIND_LABEL = { capstone: 'Capstone Project', business_plan: 'Business Launch Plan', assignment: 'Assignment' };

export default function GradingDashboard() {
  const [activeKind, setActiveKind] = useState('all');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get('/admin/grading/queue');
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = activeKind === 'all' ? rows : rows.filter(r => r.kind === activeKind);
  const downloadUrl = (s) => `${API_BASE}/submissions/${s.submission_id}/file`;

  const handleAuthDownload = async (s) => {
    const token = localStorage.getItem('trj_access_token');
    const res = await fetch(downloadUrl(s), { credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) { toast.error('Download failed'); return; }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = s.filename; document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid="faculty-grading">
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="text-xs uppercase tracking-widest text-gold font-semibold">Faculty</div>
        <h1 className="font-display font-semibold text-2xl text-navy mt-1">Grading Dashboard</h1>
        <p className="text-sm text-slate-600 mt-2">Capstone projects, business launch plans, assignments, and resubmissions awaiting review.</p>
      </div>

      <div className="p-8">
        <Tabs value={activeKind} onValueChange={setActiveKind}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">All ({rows.length})</TabsTrigger>
            <TabsTrigger value="capstone" data-testid="filter-capstone">Capstone</TabsTrigger>
            <TabsTrigger value="business_plan" data-testid="filter-business-plan">Business Plan</TabsTrigger>
            <TabsTrigger value="assignment" data-testid="filter-assignment">Assignments</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>{['Student','Kind','Course','File','Submitted','Status','Actions'].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-12">Nothing in the queue. <ClipboardList className="inline h-4 w-4 ml-1" /></td></tr>}
              {filtered.map(s => (
                <tr key={s.submission_id} className="border-t border-slate-100" data-testid={`queue-row-${s.submission_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-navy">{s.student_name}</div>
                    <div className="text-xs text-slate-500">{s.student_email}</div>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest font-semibold bg-gold/10 text-gold px-2 py-1 rounded">{KIND_LABEL[s.kind] || s.kind}</span></td>
                  <td className="px-4 py-3 text-slate-700">{s.course_title}</td>
                  <td className="px-4 py-3"><button onClick={() => handleAuthDownload(s)} className="text-navy underline inline-flex items-center gap-1 hover:text-gold"><Download className="h-3.5 w-3.5" /> {s.filename}</button></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(s.submitted_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className="text-xs uppercase tracking-widest font-semibold bg-gold/10 text-gold px-2 py-1 rounded">{s.status}</span></td>
                  <td className="px-4 py-3"><Button size="sm" className="btn-navy" onClick={() => setSelected(s)} data-testid={`review-btn-${s.submission_id}`}>Review</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ReviewDialog submission={selected} onClose={() => { setSelected(null); load(); }} onAuthDownload={handleAuthDownload} />
    </div>
  );
}

function ReviewDialog({ submission, onClose, onAuthDownload }) {
  const [feedback, setFeedback] = useState('');
  const [grade, setGrade] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (submission?.review) { setFeedback(submission.review.feedback || ''); setGrade(submission.review.grade || ''); }
    else { setFeedback(''); setGrade(''); }
  }, [submission]);

  if (!submission) return null;

  const decide = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/grading/${submission.submission_id}/review`,
        { status, feedback, grade: grade || null });
      toast.success(`Submission marked ${status}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save review');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review submission</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 bg-trjlight p-4 rounded">
            <Row k="Student" v={`${submission.student_name} (${submission.student_email})`} />
            <Row k="Kind" v={KIND_LABEL[submission.kind] || submission.kind} />
            <Row k="Course" v={submission.course_title} />
            <Row k="Submitted" v={new Date(submission.submitted_at).toLocaleString()} />
            <Row k="File" v={<button onClick={() => onAuthDownload(submission)} className="text-navy underline inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> {submission.filename}</button>} />
            <Row k="Status" v={submission.status} />
          </div>
          {submission.student_note && (
            <div><div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Student note</div><div className="mt-1 text-slate-700 whitespace-pre-wrap">{submission.student_note}</div></div>
          )}
          <div><Label>Feedback to student</Label><Textarea rows={4} value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="review-feedback" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Grade (optional)</Label><Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="A / 95 / Pass" data-testid="review-grade" /></div>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" className="text-amber-700 border-amber-200 hover:bg-amber-50" onClick={() => decide('revision_required')} disabled={busy} data-testid="decide-revision"><RotateCcw className="h-4 w-4 mr-1" /> Request revision</Button>
          <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={() => decide('rejected')} disabled={busy} data-testid="decide-reject"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
          <Button className="bg-trjgreen text-white hover:bg-trjgreen/90" onClick={() => decide('approved')} disabled={busy} data-testid="decide-approve"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return <div><div className="text-xs uppercase tracking-widest text-slate-500">{k}</div><div className="text-navy font-semibold text-sm mt-0.5">{v}</div></div>;
}
